// public/v3/src/lib/time-logs-bhi-predicate.js — pure operational-row predicate
// for Time Logs.
//
// The program filter ∈ {'ccm', 'bhi', 'pain', 'pin', 'all', undefined} drives which
// patients are operational for the current Time Logs view:
//   - 'ccm' → CCM-enrolled patients with CCM consent signed
//   - 'bhi' → BHI-enrolled patients with BHI consent signed
//   - 'pin' → PIN-enrolled patients (future dedicated lens support)
//   - 'all' (default for undefined) → CCM/BHI union; Pain/PIN use program lines
//
// Always requires the patient to be active, consent-not-declined, and
// not Elation-excluded (the existing `isWorkflowPatient` gate from
// workflow.js).
//
// Pure function — no React, no fetches. CommonJS so Jest can read it.

const {
  isWorkflowPatient,
  isEnrolledProgram,
  isBhiEnrolledPatient,
} = require('./workflow.js');
const { isPainEnrolledPatient } = require('./pain-billing.js');
const { isPinEnrolledPatient } = require('./pin-billing.js');

// Note: this predicate sits between the slot-occupancy gate (enrollment
// only) and the strict billing gate (workflow.js#isBhiOperationalRow —
// enrollment + consent + initial assessment). Time Logs surfaces enrolled
// patients with consent on file but BEFORE the initial assessment exists
// — because initial assessment is recorded IN this view
// (BhiAssessmentModal). Consent capture lives in Onboard — the Accept
// button auto-attests verbal consent atomically with enrollment, so any
// patient who is enrolled has already attested consent. The 'pending'
// state shouldn't reach Time Logs through the Onboard flow; the gate
// here is defensive against legacy / out-of-band enrollments.
//
// Both verbal and written count: BHI carries bhiConsent.source ('verbal'
// | 'written'); CCM collapses both into the literal 'signed'.
function operationalForFilter(p, programFilter) {
  if (!p || !p._raw) return false;
  if (!isWorkflowPatient(p._raw)) return false;
  const ccmAdmit = isEnrolledProgram(p._raw.billing && p._raw.billing.program)
    && p._raw.consent === 'signed';
  const bhiAdmit = isBhiEnrolledPatient(p._raw)
    && !!(p._raw.bhiConsent && p._raw.bhiConsent.signed === true);
  // Pain (CPM) rides its OWN dedicated consent record (raw.pain.consent, with
  // the coinsurance + right-to-discontinue disclosures) — global CCM consent no
  // longer qualifies CPM (P0.2, matching preset-program.js's pain gate). A
  // row-enrolled CPM patient with a complete raw.pain but a pending GLOBAL
  // consent must still reach Time Logs to log CPM time (codex finding 6).
  const painAdmit = isPainEnrolledPatient(p._raw)
    && !!(p._raw.pain && p._raw.pain.consent && p._raw.pain.consent.signed === true);
  const pinAdmit = isPinEnrolledPatient(p._raw);
  if (programFilter === 'bhi') return bhiAdmit;
  if (programFilter === 'ccm') return ccmAdmit;
  if (programFilter === 'pain') return painAdmit;
  if (programFilter === 'pin') return pinAdmit;
  // 'all' or undefined → CCM∪BHI, deliberately WITHOUT pain or PIN: the All lens's
  // minute basis is ccm+bhi (metCountsByProgram recovers CCM as total−bhi),
  // so admitting pain-only rows or mixing pain minutes/targets in would
  // corrupt the split stat. The pain caseload is the pain lens's view.
  return ccmAdmit || bhiAdmit;
}

// Per-program billing-time target lookup for a Time Logs row.
//
// Reads p.targets[slot] (set by adaptPatient in time-logs.jsx). The slot is
// derived from the chrome programFilter:
//   - 'bhi' → BHI target (20 for 99484, 0 for G0570 APCM-fallback months)
//   - 'ccm' → CCM target (20 for 99490, 30 for 99491, etc.)
//   - 'pin' → PIN target (60 for G0023)
//   - 'all' → max(ccm, bhi) so row highlight reflects the larger obligation
//   - undefined → CCM target (default)
//
// Back-compat: if the row pre-dates the per-slot targets shape, falls back
// to p.target (legacy CCM-only field). Returns 0 for null/missing rows.
//
// When additional slots (RPM, PCM) land, add a 'rpm' / 'pcm' case here and
// have adaptPatient populate the matching key in p.targets.
function targetForRow(p, programFilter) {
  if (!p) return 0;
  if (!p.targets) return p.target || 0;
  if (programFilter === 'all') {
    // No pain/PIN term: the All lens's logged basis excludes those minutes
    // (see operationalForFilter), so either target would mislabel met rows.
    return Math.max(p.targets.ccm || 0, p.targets.bhi || 0);
  }
  const slot = programFilter === 'bhi' ? 'bhi'
    : programFilter === 'pain' ? 'pain'
    : programFilter === 'pin' ? 'pin'
    : 'ccm';
  return p.targets[slot] || 0;
}

module.exports = { operationalForFilter, targetForRow };
